import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

import { MaterialModule } from '@core/modules';
import { AuthService, LoaderService, MultilingualService } from '@core/services';
import {
  AppCenterAnalytics,
  AppInsightsService,
  CmsService,
  MemberService,
} from '@shared/services';
import { AppConstants } from '@root/app/app.constants';
import { LogoComponent } from '@shared/components';
import { SetImagePathDirective } from '@shared/directives';
import { emailPattern } from '@shared/utils';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-forgot-username',
  templateUrl: './forgot-username.component.html',
  styleUrls: ['./forgot-username.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    LogoComponent,
    SetImagePathDirective,
  ],
})
export class ForgotUsernameComponent implements OnInit {
  @ViewChild('success', { read: TemplateRef })
  successTemplate!: TemplateRef<any>;
  @ViewChild('error', { read: TemplateRef }) errorTemplate!: TemplateRef<any>;

  readonly emailControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(emailPattern)],
  });

  readonly isError = signal(false);
  readonly isSuccess = signal(false);
  readonly isLoading = signal(false);
  readonly hideOkButton = signal(false);

  readonly queryParams = toSignal(this.route.queryParams, {
    initialValue: {} as Record<string, unknown>,
  });

  readonly isSubmitDisabled = computed(() => this.emailControl.invalid);

  constructor(
    private readonly memberService: MemberService,
    private readonly appInsightsService: AppInsightsService,
    private readonly route: ActivatedRoute,
    public readonly multilingualService: MultilingualService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly cmsService: CmsService,
    private readonly loader: LoaderService
  ) {
    effect(() => {
      const redirect = this.queryParams().redirect;
      this.hideOkButton.set(redirect ? JSON.parse(String(redirect)) : false);
    });
  }

  ngOnInit(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      AppCenterAnalytics.FORGOT_USERNAME_PAGE_VIEWED
    );

    this.emitEventForMobileView();
  }

  sendMail(): void {
    if (this.emailControl.invalid) return;

    this.isLoading.set(true);
    this.isError.set(false);
    this.isSuccess.set(false);

    this.memberService.sendMail(this.emailControl.value).subscribe({
      next: () => {
        this.appInsightsService.trackPageViewWithEmailProperties(
          AppCenterAnalytics.USERNAME_SENT_VIA_EMAIL,
          this.emailControl.value,
          false
        );
        this.isLoading.set(false);
        this.isSuccess.set(true);
      },
      error: () => {
        this.appInsightsService.trackPageViewWithEmailProperties(
          AppCenterAnalytics.FORGOT_USERNAME_ERROR,
          this.emailControl.value,
          true,
          'This email is not the registered email in your profile. Please contact the customer support or try again with your registered email address.'
        );
        this.isLoading.set(false);
        this.isError.set(true);
      },
    });
  }

  navigateBack(event: boolean): void {
    if (event) {
      this.appInsightsService.trackPageViewWithEmailProperties(
        AppCenterAnalytics.FORGOT_USERNAME_OK_CLICKED,
        this.emailControl.value,
        false
      );
    }
    this.loader.show();
    this.authService.navigateToLoginPage(AppConstants.LANGUAGE.DEFAULT);
  }

  getTechnicalSupportNumber(): Observable<string> {
    return this.cmsService.getTechnicalSupportNumber();
  }

  private emitEventForMobileView(): void {
    // post message to react native web view to manage header for the given page
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        url: this.router.url,
        type: 'WEB_B2C_FORM_LOADED',
      })
    );
  }
}
